/**
 * Resolved document head after a client navigation to a public detail page.
 * Until this returns, the caller keeps the head already on the document.
 */

import { useEffect, useState } from "react";
import {
  normalizePublicSeoPath,
  parsePublicSeoDetailPath,
} from "../../shared/seo/publicSeoDetailPath";

export type ResolvedSeoHead = {
  path: string;
  title: string;
  description: string;
  canonical: string;
  robots: string;
  source: "registry" | "auto";
};

export type ResolvedSeoStatus = "idle" | "loading" | "ready" | "error";

export function useResolvedSeo(pathname: string, enabled: boolean): {
  status: ResolvedSeoStatus;
  head: ResolvedSeoHead | null;
} {
  const [status, setStatus] = useState<ResolvedSeoStatus>("idle");
  const [head, setHead] = useState<ResolvedSeoHead | null>(null);

  useEffect(() => {
    if (!enabled) {
      setStatus("idle");
      setHead(null);
      return;
    }
    const path = parsePublicSeoDetailPath(pathname);
    if (!path) {
      setStatus("error");
      setHead(null);
      return;
    }
    const controller = new AbortController();
    setStatus("loading");
    setHead(null);
    fetch(`/api/seo/resolved?path=${encodeURIComponent(path)}`, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("resolved seo failed");
        return (await response.json()) as ResolvedSeoHead;
      })
      .then((data) => {
        if (normalizePublicSeoPath(data.path) !== path || !data.title) {
          throw new Error("resolved seo mismatch");
        }
        setHead(data);
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setHead(null);
        setStatus("error");
      });
    return () => controller.abort();
  }, [enabled, pathname]);

  return { status, head };
}
